import { FC } from 'react';
import { useFlag } from '@reflag/react-sdk';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Column, Row } from 'further-ui/components/Layout';
import BasicSelect from 'further-ui/components/BasicSelect';
import DateRangePicker from 'further-ui/components/DateRangePicker';
import { useFilters } from 'further-ui/hooks';
import { CalendarDayRange } from 'further-types/utils';
import { IncomeStatementDateOption } from 'further-types/lp/general-ledger';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import { SyncScrollProvider } from 'contexts/SyncScrollContext';
import PageLoader from 'components/PageLoader';
import PageFilters, { Filter } from 'components/new/PageFilters';
import downloadXlxs from 'utils/downloadXlxs';
import api from 'lib/trpcClient';
import AccountingTable from '../components/AccountingTable';
import AccountingSection from '../components/AccountingSection';
import getColumnHeaders from './helpers/getColumnHeaders';
import getColumns from './helpers/getColumns';
import {
  getIncomeOrExpensesCustomRow,
  getRealisedAndUnrealisedGainLossCustomRow,
  getOtherRealisedAndUnrealisedFxGainLossCustomRow,
  getNetOperatingIncomeLossCustomRow,
  getNetIncomeLossCustomRow,
} from './helpers/getCustomRow';
import { CalendarDay } from 'further-ui/utils';

const pageId = 'income-statement';

const incomeStatementDateLabels: Record<IncomeStatementDateOption, string> = {
  [IncomeStatementDateOption.CurrentQuarter]: 'Current quarter',
  [IncomeStatementDateOption.LastQuarter]: 'Last quarter',
  [IncomeStatementDateOption.FinancialYearToDate]: 'Financial year-to-date',
  [IncomeStatementDateOption.LastFinancialYear]: 'Last financial year',
  [IncomeStatementDateOption.Custom]: 'Custom',
};

const incomeStatementDateOptions = Object.values(IncomeStatementDateOption).map(
  (option) => ({
    label: incomeStatementDateLabels[option],
    value: option,
  }),
);

const IncomeStatement: FC = () => {
  const { isEnabled: showOtherAccounting } = useFlag('other-accounting');
  const { selectedVehicleId, vehicle } = useLayoutContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { filters, handleChangeFilters } = useFilters<{
    dateOption: IncomeStatementDateOption;
    customDateRange?: CalendarDayRange;
  }>(pageId, {
    defaultFilters: {
      dateOption: IncomeStatementDateOption.LastQuarter,
      customDateRange: undefined,
    },
  });
  const { dateOption, customDateRange } = filters;
  const isCustomDateRange = dateOption === IncomeStatementDateOption.Custom;
  const isCustomDateRangeUnset =
    isCustomDateRange &&
    (!customDateRange?.startDate || !customDateRange?.endDate);

  const customRangeComplete =
    !isCustomDateRange ||
    (!!customDateRange?.startDate && !!customDateRange?.endDate);

  const { data, isLoading, isFetching } =
    api.generalLedger.incomeStatement.useQuery(
      {
        vehicleId: selectedVehicleId ?? '',
        dateOption,
        customDateRange:
          isCustomDateRange && customDateRange
            ? {
                startDate: customDateRange.startDate,
                endDate: customDateRange.endDate,
              }
            : undefined,
      },
      {
        enabled:
          showOtherAccounting && !!selectedVehicleId && customRangeComplete,
      },
    );

  const exportIncomeStatement =
    api.generalLedger.exportIncomeStatement.useMutation(
      useHandleTrpcMutation<{ file: string; fileName: string }>()({
        onSuccess: ({ file, fileName }) => {
          downloadXlxs(file, fileName);
        },
      }),
    );

  if (!showOtherAccounting) {
    return null;
  }

  const handleExport = () => {
    if (!customRangeComplete) {
      return;
    }
    exportIncomeStatement.mutate({
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDateRange:
        dateOption === IncomeStatementDateOption.Custom && customDateRange
          ? {
              startDate: customDateRange.startDate,
              endDate: customDateRange.endDate,
            }
          : undefined,
    });
  };

  if (isLoading || !vehicle) {
    return <PageLoader />;
  }

  const { baseCurrency } = vehicle;

  const sections = (data?.sections ?? []).map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => item.period1 !== 0 || item.period2 !== 0 || item.period3 !== 0,
    ),
  }));

  const today = new CalendarDay(new Date());
  const periodEndDate = data?.periodEndDate?.isAfter(today)
    ? today
    : data?.periodEndDate;
  const [header1, header2, header3] = getColumnHeaders(
    dateOption,
    data?.periodStartDate,
    data?.fyStartDate,
    periodEndDate,
    data?.inceptionDate,
  );
  const columns = getColumns({
    baseCurrency,
    header1,
    header2,
    header3,
  });

  return (
    <Column spacing="lg" fullHeight>
      <Row centered spacing="sm" justify="space-between" breakpoint="md">
        <Column spacing="xs">
          <Typography variant="h5" color="grey.900">
            Income statement
          </Typography>
          <Typography variant="body2" noWrap={!isMobile}>
            {vehicle.vehicleName} for the periods ending{' '}
            {isCustomDateRange
              ? isCustomDateRangeUnset
                ? '...'
                : customDateRange?.endDate.toReadable()
              : incomeStatementDateLabels[dateOption].toLowerCase()}
            {isCustomDateRange ? (isCustomDateRangeUnset ? '' : '.') : '.'}
          </Typography>
        </Column>
        <PageFilters
          justify="flex-end"
          align="center"
          actions={[
            {
              label: 'Export',
              variant: 'contained',
              onClick: handleExport,
              disabled: isCustomDateRangeUnset,
            },
          ]}
        >
          <Filter size="sm">
            <BasicSelect
              fullWidth
              options={incomeStatementDateOptions}
              value={dateOption}
              onChange={(newDateOption) =>
                handleChangeFilters({
                  dateOption: newDateOption,
                  customDateRange:
                    newDateOption !== IncomeStatementDateOption.Custom
                      ? undefined
                      : customDateRange,
                })
              }
              placeholder="Select date"
            />
          </Filter>
          {isCustomDateRange ? (
            <Filter size="sm">
              <DateRangePicker
                dateRange={customDateRange}
                onDateRangeChange={(dateRange) => {
                  handleChangeFilters({ customDateRange: dateRange });
                }}
                placeholder="Select custom date range"
              />
            </Filter>
          ) : null}
        </PageFilters>
      </Row>
      <SyncScrollProvider>
        <Column fullHeight>
          <AccountingTable
            columns={columns}
            rows={[]}
            isLoading={isFetching}
            headerRowLineHeight={16}
            headerRowPaddingY={8}
          />
          {isFetching
            ? null
            : sections.map((section, sectionIndex) => (
                <AccountingSection
                  key={sectionIndex}
                  title={section.title}
                  titleVariant="h6"
                  expandable={false}
                  noTopBorder
                >
                  <AccountingTable
                    columns={columns}
                    rows={section.items.map((item) => ({
                      id: item.label,
                      ...item,
                    }))}
                    borderless
                    hideHeader
                    indentation={48}
                    customRowVerticalBorder
                    displayFooterIfEmptyTable
                    customFooterRow={
                      section.title === 'Income' || section.title === 'Expenses'
                        ? getIncomeOrExpensesCustomRow(baseCurrency, section)
                        : section.title ===
                          'Realised and unrealised gain / (loss) on investments'
                        ? getRealisedAndUnrealisedGainLossCustomRow(
                            baseCurrency,
                            section,
                          )
                        : section.title ===
                          'Other realised and unrealised FX gain / (loss)'
                        ? getOtherRealisedAndUnrealisedFxGainLossCustomRow(
                            baseCurrency,
                            section,
                          )
                        : undefined
                    }
                  />
                  {section.title === 'Expenses' ? (
                    <AccountingTable
                      columns={columns}
                      rows={[]}
                      topBorderOnly
                      hideHeader
                      displayFooterIfEmptyTable
                      customRowVerticalBorder
                      customFooterRow={getNetOperatingIncomeLossCustomRow(
                        baseCurrency,
                        data?.netOperatingIncome,
                      )}
                    />
                  ) : null}
                  {sectionIndex === sections.length - 1 ? (
                    <AccountingTable
                      columns={columns}
                      rows={[]}
                      topBorderOnly
                      hideHeader
                      displayFooterIfEmptyTable
                      customRowVerticalBorder
                      customFooterRow={getNetIncomeLossCustomRow(
                        baseCurrency,
                        data?.netIncome,
                      )}
                    />
                  ) : null}
                </AccountingSection>
              ))}
        </Column>
      </SyncScrollProvider>
    </Column>
  );
};

export default IncomeStatement;
